import React, { useState } from 'react'
import { T } from '@transifex/react'
import {
  Card,
  Icon,
  Button,
  ToastNotifications,
} from '@lightspeed/design-system-react'
import { Currency } from '@vend/business-components'
import {
  IconCheckCircleSolid20,
  IconScheduleSolid20,
} from '@lightspeed/unified-react-components'
import { PaymentRequestQueryResponse } from '../../../../functions/invoicing/fetch-invoicing-payment-requests'
import {
  getPaymentRequestViewURL,
  formatDate,
} from '../../../../functions/invoicing/payment-request-resources'
import { cancelPaymentRequest } from '../../../../functions/invoicing/cancel-payment-request'
import { useRetailerSettingsManager } from '../../../../ng-compat/hooks'
import { PaymentRequestStatus } from '../../../../functions/invoicing/payment-request-types'
import { useAppSelector } from '../../../../state/hooks'
import { selectPaymentRequestsAutoCancelled } from '../../../../state/slices/currentSale/currentSaleSlice'
import { PaymentRequestOverpaymentWarning } from './PaymentRequestOverpaymentWarning'
import { usePaymentRequestCalculations } from './hooks/usePaymentRequestCalculations'
import { StatusBadge } from './StatusBadge'
import { PaymentRequestCancelledConfirmationBanner } from './PaymentRequestCancelledConfirmationBanner'

const getStatusIcon = (status: string) => {
  switch (status) {
    case PaymentRequestStatus.SENT:
      return <IconScheduleSolid20 className="vd-text-info" />
    case PaymentRequestStatus.PAID:
      return <IconCheckCircleSolid20 className="vd-text-success" />
    default:
      return null
  }
}

const getPaidDate = (request: PaymentRequestQueryResponse): string => {
  const capturedPayment = request.payments?.find(
    payment => payment.status === 'captured'
  )

  return capturedPayment ? formatDate(capturedPayment.capturedAt) : ''
}

const getStatusText = (request: PaymentRequestQueryResponse) => {
  // Convert from cents to dollars
  const amount = request.requestedAmount / 100

  if (request.status === PaymentRequestStatus.SENT) {
    const sentDate = formatDate(request.createdAt)
    return (
      <T
        _str="A {amount} payment request was sent on {date}."
        _context="Payment request pending status"
        amount={<Currency value={amount} />}
        date={sentDate}
      />
    )
  }

  if (request.status === PaymentRequestStatus.PAID) {
    const paidDate = getPaidDate(request)
    return (
      <T
        _str="{amount} request paid on {date}"
        _context="Payment request paid status"
        amount={<Currency value={amount} />}
        date={paidDate}
      />
    )
  }

  return null
}
interface Props {
  paymentRequests: PaymentRequestQueryResponse[] // List of payment requests for the current sale
  onRefetch?: () => Promise<void> // Callback to refetch payment requests
  outstandingBalance?: number // Outstanding balance of the sale in dollars for overpayment calculation
  amountTendered?: string // Amount tendered on the payment panel in dollars
}

export const PaymentRequestSummaryCard: React.FC<Props> = ({
  paymentRequests,
  onRefetch,
  outstandingBalance,
  amountTendered,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [cancellingIds, setCancellingIds] = useState<Set<string>>(new Set())
  const retailerSettingsManager = useRetailerSettingsManager()
  const retailer = retailerSettingsManager.get()

  const hasAutoCancelled = useAppSelector(selectPaymentRequestsAutoCancelled)

  const {
    visibleRequests,
    pendingRequests,
    overpaymentThresholdDollars,
    totalPendingAmountDollars,
  } = usePaymentRequestCalculations(paymentRequests, outstandingBalance)

  // Display a cancell confirmation banner only if payment requests were auto-cancelled
  // For manual cancellations, the success toast is shown instead
  if (hasAutoCancelled) {
    return <PaymentRequestCancelledConfirmationBanner />
  }

  if (visibleRequests.length === 0) {
    return null
  }

  // Determine if the summary card is expandable (more than 1 request),
  // if is expandable -> will display header, status badge and expand/collapse arrow icon
  // if not expandable ex: visibleRequests.length == 1 -> will directly show the single request details, no header, status badge or expand/collapse icon
  // Note: isExpandable determines IF card can expand, isExpanded tracks IF it's currently expanded
  const isExpandable = visibleRequests.length > 1

  // Show overpayment warning when:
  // 1. There are pending payment requests
  // 2. Outstanding balance is defined (sale has balance to pay)
  // 3. Amount tendered is defined (user has entered payment amount)
  // 4. Amount tendered exceeds the overpayment threshold (outstanding balance minus pending requests total)
  const shouldShowWarning =
    pendingRequests.length > 0 &&
    outstandingBalance !== undefined &&
    amountTendered !== undefined &&
    parseFloat(amountTendered) > overpaymentThresholdDollars

  const handleViewClick = (request: PaymentRequestQueryResponse) => {
    const viewUrl = getPaymentRequestViewURL(request.proposalHash, retailer)
    window.open(viewUrl, '_blank', 'noopener,noreferrer')
  }

  const handleCancelClick = async (
    request: PaymentRequestQueryResponse,
    event: React.MouseEvent
  ) => {
    event.stopPropagation() // Prevent expanding/collapsing when clicking cancel

    // Mark as cancelling to show loading state
    setCancellingIds(prev => new Set(prev).add(request.id))

    try {
      const result = await cancelPaymentRequest(request.id)

      if (result.success) {
        ToastNotifications.success(
          `Payment request #${request.id} has been cancelled.`
        )
        // Refetch to update the UI
        if (onRefetch) {
          await onRefetch()
        }
      } else {
        ToastNotifications.negative(
          result.error || 'Failed to cancel payment request'
        )
      }
    } catch (error) {
      ToastNotifications.negative('Failed to cancel payment request')
    } finally {
      // Remove from cancelling set
      setCancellingIds(prev => {
        const next = new Set(prev)
        next.delete(request.id)
        return next
      })
    }
  }

  return (
    <Card className="payment-request-summary-card">
      {/* header */}
      {isExpandable && (
        <div
          className="payment-request-summary-card-header"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="vd-flex" style={{ gap: 12 }}>
            <strong>
              <T
                _str="Payment requests"
                _context="Payment requests section title"
              />
            </strong>
            <StatusBadge
              totalPendingAmountDollars={totalPendingAmountDollars}
            />
          </div>
          <Icon
            icon={isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}
            className="vd-mr0"
          />
        </div>
      )}

      {/* overpayment warning   */}
      {shouldShowWarning && (
        <PaymentRequestOverpaymentWarning
          pendingCount={pendingRequests.length}
          totalPendingAmount={totalPendingAmountDollars}
          thresholdAmount={overpaymentThresholdDollars}
        />
      )}

      {/* 
        Payment requests list - show when:
        1. isExpanded = true (user has clicked to expand the card)
        2. OR visibleRequests.length === 1 (single request always shown, no expansion needed)
      */}
      {(isExpanded || visibleRequests.length === 1) && (
        <div className="vd-flex vd-flex--column" style={{ gap: 24 }}>
          {visibleRequests.map(request => (
            <div key={request.id} className="vd-flex" style={{ gap: 8 }}>
              {getStatusIcon(request.status)}
              <div
                className="vd-flex vd-flex--column vd-align-left"
                style={{ gap: 8 }}
              >
                {getStatusText(request)}
                <div className="vd-flex" style={{ gap: 12 }}>
                  <Button
                    link
                    className="vd-flex vd-flex--align-end vd-link"
                    style={{ gap: 4, flex: 'none' }}
                    onClick={() => handleViewClick(request)}
                  >
                    <T _str="View" _context="Payment request view link" />
                    <Icon
                      icon="fa-external-link"
                      style={{ textDecoration: 'none' }}
                    />
                  </Button>
                  {request.status === PaymentRequestStatus.SENT && (
                    <Button
                      link
                      className="vd-link vd-link--secondary"
                      onClick={e => handleCancelClick(request, e)}
                      disabled={cancellingIds.has(request.id)}
                    >
                      <T _str="Cancel" _context="Payment request cancel link" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}
